import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'

import { GET, httpOk } from 'helpers/api'
import { formatMoney } from 'helpers/helpers'

// Segment ranglari (funnel kartalar)
const SEG_COLORS = ['#2563eb', '#7c3aed', '#0ea5e9', '#f59e0b', '#10b981', '#64748b', '#ec4899']

function shortMoney(v) {
	v = Number(v) || 0
	const a = Math.abs(v)
	if (a >= 1e9) return (v / 1e9).toFixed(2) + ' mlrd'
	if (a >= 1e6) return (v / 1e6).toFixed(1) + ' mln'
	return formatMoney(v)
}
const num = (v) => Number(v) || 0
const pct = (a, b) => b > 0 ? Math.round((a / b) * 100) : 0
const AKB_DAYS = [30, 60, 90]

// Mahsulot turlari (afinlik ustunlari)
const PTYPES = [{ id: 4, k: 'Profnastil' }, { id: 3, k: 'Aksessuar' }, { id: 5, k: 'Tunikofon' }, { id: 2, k: 'Rulon' }, { id: 1, k: 'Produkt' }]

function Index() {
	const { t } = useTranslation()
	const [d, setD] = useState({ segments: [], regions: [], rfm: [], pareto: {}, payment_discipline: [], affinity: [], sellers: [], watchlist: [], dynamics: [], top_clients: [], cohort: [] })
	const [akbDays, setAkbDays] = useState(60)
	const [m360, setM360] = useState({ show: false, loading: false, profile: null, activity: [] })
	const [mSeg, setMSeg] = useState({ show: false, loading: false, seg: '', title: '', clients: [] })

	async function load(days) {
		const res = await GET('/analytics/customer', { akb_days: days }, { loader: true })
		if (httpOk(res)) setD(res.data)
	}
	useEffect(() => { load(akbDays) }, [akbDays]) // eslint-disable-line react-hooks/exhaustive-deps

	async function open360(id) {
		if (!id) return
		setM360({ show: true, loading: true, profile: null, activity: [] })
		const res = await GET(`/analytics/customer/${id}`, {}, { loader: false })
		if (httpOk(res)) setM360({ show: true, loading: false, profile: res.data.profile, activity: res.data.activity || [] })
		else setM360({ show: false, loading: false, profile: null, activity: [] })
	}

	// RFM kartasi bosilganda — o'sha segmentdagi mijozlar ro'yxati
	async function openSeg(k, title) {
		setMSeg({ show: true, loading: true, seg: k, title, clients: [] })
		const res = await GET(`/analytics/customer/segment/${k}`, {}, { loader: false })
		if (httpOk(res)) setMSeg({ show: true, loading: false, seg: k, title, clients: res.data.clients || [] })
		else setMSeg({ show: false, loading: false, seg: '', title: '', clients: [] })
	}

	const segName = (s) => (typeof s === 'string' ? s : s?.segment) || t('region_no_type')
	const tot = d.segments.reduce((a, s) => ({
		okb: a.okb + num(s.okb), akb: a.akb + num(s.akb), kkb: a.kkb + num(s.kkb),
		doimiy: a.doimiy + num(s.doimiy), savdo: a.savdo + num(s.savdo), foyda: a.foyda + num(s.foyda),
	}), { okb: 0, akb: 0, kkb: 0, doimiy: 0, savdo: 0, foyda: 0 })

	const chain = [
		{ label: 'ОКБ', sub: t('okb_full'), val: tot.okb, of: null, c: '#64748b' },
		{ label: 'АКБ', sub: `${akbDays} ${t('day_short')}`, val: tot.akb, of: pct(tot.akb, tot.okb), c: '#2563eb' },
		{ label: 'ККБ', sub: t('kkb_full'), val: tot.kkb, of: pct(tot.kkb, tot.okb), c: '#7c3aed' },
		{ label: t('regular_clients'), sub: '≥3/90' + t('day_short'), val: tot.doimiy, of: pct(tot.doimiy, tot.okb), c: '#10b981' },
	]

	// Segment funnel kartalar uchun — badge mantiqi
	const segMaxOkb = Math.max(...d.segments.map(s => num(s.okb)), 0)
	const segMaxDpct = Math.max(...d.segments.filter(s => s.segment).map(s => pct(num(s.doimiy), num(s.okb))), 0)
	function segBadge(s) {
		if (!s.segment) return { txt: t('seg_badge_mark'), c: 'bg-secondary' }
		const okb = num(s.okb), dpct = pct(num(s.doimiy), okb)
		if (okb === segMaxOkb) return { txt: t('seg_badge_volume'), c: 'bg-primary' }
		if (dpct === segMaxDpct) return { txt: t('seg_badge_loyal'), c: 'bg-success' }
		if (dpct < 10) return { txt: t('seg_badge_onetime'), c: 'bg-warning text-dark' }
		return { txt: t('seg_badge_stable'), c: 'bg-info' }
	}

	// RFM -> obyekt
	const rfmMap = {}; d.rfm.forEach(r => { rfmMap[r.seg] = num(r.cnt) })
	const rfmCells = [
		{ k: 'champions', t: 'Champions', bg: '#ecfdf5', bd: '#a7f3d0', dot: '#059669' },
		{ k: 'sodiq', t: t('rfm_loyal'), bg: '#eff6ff', bd: '#bfdbfe', dot: '#2563eb' },
		{ k: 'yangi', t: t('rfm_new'), bg: '#f5f3ff', bd: '#ddd6fe', dot: '#7c3aed' },
		{ k: 'xavf_ostida', t: t('rfm_at_risk'), bg: '#fffbeb', bd: '#fde68a', dot: '#d97706' },
		{ k: 'yoqotilgan', t: t('rfm_lost'), bg: '#fef2f2', bd: '#fecaca', dot: '#dc2626' },
		{ k: 'kuzatuv', t: t('rfm_watch'), bg: '#f8fafc', bd: '#e2e8f0', dot: '#94a3b8' },
	]

	// Afinlik pivot
	const affSeg = {}
	d.affinity.forEach(a => {
		const key = a.seg_key
		if (!affSeg[key]) affSeg[key] = { name: a.segment, total: 0, pt: {} }
		affSeg[key].pt[a.pt] = num(a.rev); affSeg[key].total += num(a.rev)
	})
	const affRows = Object.values(affSeg).sort((a, b) => b.total - a.total).slice(0, 6)

	// Kohort pivot
	const cohRows = {}
	d.cohort.forEach(c => { if (!cohRows[c.cohort]) cohRows[c.cohort] = {}; cohRows[c.cohort][c.period] = num(c.pct) })
	const cohKeys = Object.keys(cohRows).sort()
	const cohGreen = (p) => p == null ? { background: '#f8fafc', color: '#94a3b8' }
		: { background: `rgba(5,150,105,${0.12 + (p / 100) * 0.8})`, color: p > 55 ? '#fff' : '#065f46' }

	// Dinamika chart nuqtalari
	const dyn = d.dynamics
	const maxAkb = Math.max(...dyn.map(x => num(x.akb)), 1)
	const dynPts = dyn.map((x, i) => {
		const px = dyn.length > 1 ? (i / (dyn.length - 1)) * 600 : 0
		const py = 110 - (num(x.akb) / maxAkb) * 95
		return `${px},${py}`
	}).join(' ')

	// Kvadrant: qarzdorlar + eng foydali toza mijozlar (0 markazli foyda o'qi).
	// 90-foizli masshtab — bitta chetki qiymat (masalan -164 mln zararli mijoz) grafikni ezib qo'ymasin.
	const quad = d.quadrant || []
	const clampN = (v, lo, hi) => Math.max(lo, Math.min(hi, v))
	const pctl = (arr, p) => { const s = arr.slice().sort((a, b) => a - b); return s.length ? (s[Math.min(s.length - 1, Math.floor(s.length * p))] || 1) : 1 }
	const scaleF = pctl(quad.map(x => Math.abs(num(x.foyda))), 0.9)
	const scaleQ = pctl(quad.map(x => Math.abs(num(x.qarz))), 0.9)
	const maxSvQ = Math.max(...quad.map(x => num(x.savdo)), 1)

	return (
		<>
			<div className="page-header d-flex justify-content-between flex-wrap gap-2">
				<h4 className="vertical-center">{t('customer_analytics')} [{tot.okb}]</h4>
				<div className="d-flex align-items-center gap-2">
					<span className="text-muted" style={{ fontSize: '13px' }}>{t('akb_period')}:</span>
					<div className="btn-group">
						{AKB_DAYS.map(x => (
							<button key={x} type="button" className={`btn btn-sm ${akbDays === x ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setAkbDays(x)}>{x} {t('day_short')}</button>
						))}
					</div>
				</div>
			</div>

			{/* KPI zanjiri */}
			<div className="row g-3 mb-3">
				{chain.map((c, i) => (
					<div className="col-6 col-lg-3" key={i}>
						<div className="card h-100"><div className="card-body py-3">
							<div className="d-flex justify-content-between align-items-center">
								<span className="fw-600" style={{ color: c.c }}>{c.label}</span>
								{c.of !== null && <span className="badge" style={{ background: c.c + '22', color: c.c }}>{c.of}%</span>}
							</div>
							<div className="fw-bold" style={{ fontSize: '26px' }}>{c.val}</div>
							<div className="text-muted" style={{ fontSize: '12px' }}>{c.sub}</div>
						</div></div>
					</div>
				))}
			</div>

			{/* Segment funnel kartalar */}
			<h6 className="fw-bold text-uppercase text-muted mb-2" style={{ fontSize: '11.5px', letterSpacing: '.05em' }}>{t('segments_by_category')}</h6>
			<div className="row g-2 mb-3">
				{d.segments.map((s, i) => {
					const okb = num(s.okb), akb = num(s.akb), doimiy = num(s.doimiy)
					const color = SEG_COLORS[i % SEG_COLORS.length], b = segBadge(s)
					const row = (label, v, w, cl, dark) => (
						<div className="d-flex align-items-center gap-2 mb-1">
							<span style={{ width: '48px', fontSize: '11.5px', color: '#64748b' }}>{label}</span>
							<div style={{ flex: 1, height: '20px', background: '#f1f5f9', borderRadius: '5px', position: 'relative', overflow: 'hidden' }}>
								<div style={{ width: `${w}%`, height: '100%', background: cl, borderRadius: '5px' }}></div>
								<span style={{ position: 'absolute', right: w > 25 ? '6px' : 'auto', left: w > 25 ? 'auto' : `calc(${w}% + 6px)`, top: '50%', transform: 'translateY(-50%)', fontSize: '11.5px', fontWeight: 700, color: (w > 25 && dark) ? '#fff' : '#1e293b' }}>{v}</span>
							</div>
						</div>
					)
					return (
						<div className="col-6 col-md-4 col-lg" key={i}>
							<div className="card h-100"><div className="card-body p-3">
								<div className="d-flex align-items-center mb-2">
									<span style={{ width: '9px', height: '9px', borderRadius: '50%', background: color, display: 'inline-block', marginRight: '7px' }}></span>
									<span className="fw-600" style={{ fontSize: '13.5px' }}>{s.segment || t('region_no_type')}</span>
									<span className="ms-auto fw-600" style={{ fontSize: '12px', color: pct(doimiy, okb) < 10 ? '#dc3545' : (pct(doimiy, okb) === segMaxDpct ? '#059669' : '#64748b') }}>{pct(doimiy, okb)}%</span>
								</div>
								{row('ОКБ', okb, 100, color + '33', false)}
								{row('АКБ', akb, pct(akb, okb), color + '99', false)}
								{row('Doimiy', doimiy, pct(doimiy, okb), color, true)}
								<div className="d-flex align-items-end justify-content-between mt-2">
									<div><div className="fw-bold" style={{ fontSize: '22px', lineHeight: 1 }}>{doimiy}</div><div className="text-muted" style={{ fontSize: '11px' }}>{t('regular_clients').toLowerCase()}</div></div>
									<span className={`badge ${b.c}`} style={{ fontSize: '10px' }}>{b.txt}</span>
								</div>
							</div></div>
						</div>
					)
				})}
			</div>

			{/* Segment jadvali */}
			<div className="card mb-3"><div className="card-body">
				<h6 className="mb-3 fw-bold">{t('by_segment')}</h6>
				<div className="table-responsive"><table className="table table-bordered mb-0">
					<thead><tr>
						<th>{t('type')}</th><th className="text-center">ОКБ</th><th className="text-center">АКБ</th><th className="text-center">ККБ</th>
						<th className="text-center">{t('regular_clients')}</th><th className="text-end">{t('sales')}</th><th className="text-end">{t('profit')}</th>
						<th className="text-center">{t('margin')}</th><th className="text-end">{t('debt')}</th><th className="text-center">{t('share')}</th>
					</tr></thead>
					<tbody>
						{d.segments.map((s, i) => (
							<tr key={i}>
								<td className="fw-600">{segName(s)}</td>
								<td className="text-center">{num(s.okb)}</td><td className="text-center">{num(s.akb)}</td><td className="text-center">{num(s.kkb)}</td>
								<td className="text-center fw-600" style={{ color: '#10b981' }}>{num(s.doimiy)}</td>
								<td className="text-end text-nowrap">{shortMoney(s.savdo)}</td>
								<td className="text-end text-nowrap" style={{ color: '#059669' }}>{shortMoney(s.foyda)}</td>
								<td className="text-center">{pct(num(s.foyda), num(s.savdo))}%</td>
								<td className="text-end text-nowrap" style={{ color: '#dc3545' }}>{shortMoney(s.qarz)}</td>
								<td className="text-center">{pct(num(s.savdo), tot.savdo)}%</td>
							</tr>
						))}
					</tbody>
					<tfoot><tr className="fw-bold">
						<td>{t('total')}</td><td className="text-center">{tot.okb}</td><td className="text-center">{tot.akb}</td><td className="text-center">{tot.kkb}</td>
						<td className="text-center">{tot.doimiy}</td><td className="text-end text-nowrap">{shortMoney(tot.savdo)}</td>
						<td className="text-end text-nowrap" style={{ color: '#059669' }}>{shortMoney(tot.foyda)}</td>
						<td className="text-center">{pct(tot.foyda, tot.savdo)}%</td><td></td><td className="text-center">100%</td>
					</tr></tfoot>
				</table></div>
			</div></div>

			{/* Dinamika + Hududlar */}
			<div className="row g-3 mb-3">
				<div className="col-lg-7"><div className="card h-100"><div className="card-body">
					<h6 className="fw-bold mb-1">{t('akb_dynamics')}</h6>
					<p className="text-muted" style={{ fontSize: '12.5px' }}>{t('akb_dynamics_hint')}</p>
					<svg width="100%" height="130" viewBox="0 0 600 130" preserveAspectRatio="none" style={{ overflow: 'visible' }}>
						<polyline points={dynPts} fill="none" stroke="#2563eb" strokeWidth="2.5" strokeLinejoin="round" />
						{dyn.map((x, i) => {
							const px = dyn.length > 1 ? (i / (dyn.length - 1)) * 600 : 0
							const py = 110 - (num(x.akb) / maxAkb) * 95
							return <circle key={i} cx={px} cy={py} r="3.5" fill="#2563eb" />
						})}
					</svg>
					<div className="d-flex justify-content-between text-muted" style={{ fontSize: '11px' }}>
						{dyn.map((x, i) => <span key={i}>{x.oy?.slice(5)} · <b>{num(x.akb)}</b></span>)}
					</div>
				</div></div></div>
				<div className="col-lg-5"><div className="card h-100"><div className="card-body">
					<h6 className="fw-bold mb-1">{t('akb_by_region')}</h6>
					{d.regions.length === 0 && <div className="text-muted" style={{ fontSize: '13px' }}><i className="uil-map-marker me-1"></i>{t('akb_region_empty')}</div>}
					{d.regions.map((r, i) => {
						const mx = Math.max(...d.regions.map(x => num(x.akb)), 1)
						return (
							<div key={i} className="d-flex align-items-center gap-2 py-1">
								<span style={{ width: '120px', fontSize: '12.5px', fontWeight: 600 }} className="text-truncate">{r.region}</span>
								<div style={{ flex: 1, height: '10px', background: '#f1f5f9', borderRadius: '99px' }}>
									<div style={{ width: `${pct(num(r.akb), mx)}%`, height: '100%', background: '#2563eb', borderRadius: '99px' }}></div>
								</div>
								<span style={{ width: '70px', textAlign: 'right', fontSize: '12.5px' }}><b>{num(r.akb)}</b><span className="text-muted"> / {num(r.okb)}</span></span>
							</div>
						)
					})}
				</div></div></div>
			</div>

			{/* Sotuvchi portfeli */}
			<div className="card mb-3"><div className="card-body">
				<h6 className="fw-bold mb-1">{t('seller_portfolio')}</h6>
				<p className="text-muted mb-3" style={{ fontSize: '12.5px' }}>{t('seller_portfolio_hint')}</p>
				<div className="table-responsive"><table className="table table-bordered mb-0">
					<thead><tr>
						<th>{t('cashier')}</th><th className="text-center">ОКБ</th><th className="text-center">АКБ</th><th className="text-center">{t('coverage')}</th>
						<th className="text-center">ККБ</th><th className="text-center">{t('regular_clients')}</th><th className="text-end">{t('sales')}</th><th className="text-center">{t('change')}</th>
					</tr></thead>
					<tbody>
						{d.sellers.map((s, i) => {
							const cov = pct(num(s.akb), num(s.okb))
							const covBadge = cov >= 45 ? 'bg-success' : cov >= 35 ? 'bg-warning text-dark' : 'bg-danger'
							const oz = num(s.ozgarish)
							return (
								<tr key={i}>
									<td className="fw-600">{s.seller}</td>
									<td className="text-center">{num(s.okb)}</td><td className="text-center">{num(s.akb)}</td>
									<td className="text-center"><span className={`badge ${covBadge}`}>{cov}%</span></td>
									<td className="text-center">{num(s.kkb)}</td>
									<td className="text-center fw-600" style={{ color: '#10b981' }}>{num(s.doimiy)}</td>
									<td className="text-end text-nowrap">{shortMoney(s.savdo)}</td>
									<td className="text-center fw-600" style={{ color: oz > 0 ? '#059669' : oz < 0 ? '#dc3545' : '#64748b' }}>{oz > 0 ? '▲ +' + oz : oz < 0 ? '▼ ' + oz : '—'}</td>
								</tr>
							)
						})}
					</tbody>
				</table></div>
			</div></div>

			{/* RFM + Kvadrant */}
			<div className="row g-3 mb-3">
				<div className="col-lg-6"><div className="card h-100"><div className="card-body">
					<h6 className="fw-bold mb-1">{t('rfm_title')}</h6>
					<p className="text-muted mb-2" style={{ fontSize: '12.5px' }}>{t('rfm_hint')}</p>
					<div className="row g-2">
						{rfmCells.map((c, i) => (
							<div className="col-4" key={i}>
								<div onClick={() => openSeg(c.k, c.t)} style={{ background: c.bg, border: `1px solid ${c.bd}`, borderRadius: '10px', padding: '11px', cursor: 'pointer', transition: 'transform .1s' }}
									onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={e => e.currentTarget.style.transform = 'none'}>
									<div style={{ fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '5px' }}>
										<span style={{ width: '8px', height: '8px', borderRadius: '50%', background: c.dot, display: 'inline-block' }}></span>{c.t}
									</div>
									<div className="fw-bold d-flex align-items-center justify-content-between" style={{ fontSize: '22px' }}>
										{rfmMap[c.k] || 0}<i className="uil-angle-right-b text-muted" style={{ fontSize: '15px' }}></i>
									</div>
								</div>
							</div>
						))}
					</div>
				</div></div></div>
				<div className="col-lg-6"><div className="card h-100"><div className="card-body">
					<h6 className="fw-bold mb-1">{t('profit_debt_quadrant')}</h6>
					<p className="text-muted" style={{ fontSize: '12.5px' }}>{t('profit_debt_hint')}</p>
					<svg width="100%" height="240" viewBox="0 0 440 240">
						{/* markaz: vertikal = foyda 0 chizig'i, gorizontal = qarz darajasi ko'rsatkichi */}
						<line x1="220" y1="14" x2="220" y2="212" stroke="#e2e8f0" />
						<line x1="24" y1="112" x2="416" y2="112" stroke="#f1f5f9" />
						<text x="26" y="26" fontSize="10" fill="#dc2626">⚠ {t('quad_risky')}</text>
						<text x="414" y="205" fontSize="10" fill="#059669" textAnchor="end">✓ {t('quad_good')}</text>
						<text x="414" y="228" fontSize="9" fill="#94a3b8" textAnchor="end">{t('profit')} →</text>
						<text x="6" y="120" fontSize="9" fill="#94a3b8">↑{t('debt')}</text>
						{quad.map((x, i) => {
							const f = num(x.foyda), q = Math.abs(num(x.qarz))
							const cx = 220 + clampN(f / scaleF, -1, 1) * 186
							const cy = 205 - clampN(q / scaleQ, 0, 1) * 185
							const r = 4 + (num(x.savdo) / maxSvQ) * 11
							const debtor = num(x.qarz) < 0
							const loss = f < 0
							return <circle key={i} cx={cx} cy={cy} r={r}
								fill={debtor ? '#f59e0b' : '#10b981'} fillOpacity=".45"
								stroke={loss ? '#dc2626' : 'none'} strokeWidth={loss ? 1.4 : 0}>
								<title>{`${x.name} · ${t('profit')}: ${shortMoney(f)} · ${t('debt')}: ${shortMoney(num(x.qarz))}`}</title>
							</circle>
						})}
					</svg>
					<div className="d-flex gap-3 mt-1" style={{ fontSize: '11.5px' }}>
						<span><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: '#f59e0b', marginRight: 4 }}></span>{t('debtor')}</span>
						<span><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: '#10b981', marginRight: 4 }}></span>{t('no_debt')}</span>
						<span><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', border: '1.4px solid #dc2626', marginRight: 4 }}></span>{t('loss_making')}</span>
					</div>
				</div></div></div>
			</div>

			{/* Watchlist */}
			<div className="card mb-3"><div className="card-body">
				<div className="d-flex justify-content-between align-items-center mb-2">
					<h6 className="fw-bold mb-0"><i className="uil-exclamation-triangle text-warning me-1"></i>{t('at_risk_clients')}</h6>
					<span className="badge bg-danger">{d.watchlist.length}</span>
				</div>
				<p className="text-muted" style={{ fontSize: '12.5px' }}>{t('at_risk_hint')}</p>
				<div className="table-responsive"><table className="table table-bordered mb-0">
					<thead><tr>
						<th>{t('client')}</th><th>{t('type')}</th><th className="text-center">{t('silent_days')}</th>
						<th className="text-end">{t('lifetime_profit')}</th><th className="text-end">{t('avg_monthly')}</th><th className="text-end">{t('loss_per_month')}</th><th>{t('phone')}</th><th></th>
					</tr></thead>
					<tbody>
						{d.watchlist.map((w, i) => (
							<tr key={i}>
								<td className="fw-600">{w.name}</td><td>{w.segment || t('region_no_type')}</td>
								<td className="text-center fw-bold" style={{ color: num(w.silent) > 120 ? '#dc3545' : '#d97706' }}>{num(w.silent)}</td>
								<td className="text-end text-nowrap" style={{ color: '#059669' }}>{shortMoney(w.foyda)}</td>
								<td className="text-end text-nowrap">{shortMoney(w.ort_oylik)}</td>
								<td className="text-end text-nowrap" style={{ color: '#dc3545' }}>−{shortMoney(w.ort_oylik)}</td>
								<td className="text-nowrap text-muted">{w.phone}</td>
								<td>{w.phone && <a href={`tel:${w.phone}`} className="btn btn-sm btn-outline-success py-0 px-2" style={{ fontSize: '12px' }}><i className="uil-phone-alt me-1"></i>{t('call')}</a>}</td>
							</tr>
						))}
						{d.watchlist.length === 0 && <tr><td colSpan="8" className="text-center text-muted py-2">{t('list_empty')}</td></tr>}
					</tbody>
				</table></div>
			</div></div>

			{/* Kohort */}
			<div className="card mb-3"><div className="card-body">
				<h6 className="fw-bold mb-1">{t('cohort_retention')}</h6>
				<p className="text-muted" style={{ fontSize: '12.5px' }}>{t('cohort_hint')}</p>
				<div className="table-responsive"><table className="table mb-0" style={{ borderCollapse: 'separate', borderSpacing: '4px' }}>
					<thead><tr><th></th>{[0, 1, 2, 3, 4, 5].map(p => <th key={p} className="text-center text-muted" style={{ fontSize: '11px' }}>+{p}</th>)}</tr></thead>
					<tbody>
						{cohKeys.map(ck => (
							<tr key={ck}>
								<td className="fw-600" style={{ fontSize: '12px' }}>{ck}</td>
								{[0, 1, 2, 3, 4, 5].map(p => {
									const v = cohRows[ck][p]
									return <td key={p} className="text-center" style={{ ...cohGreen(v), borderRadius: '6px', fontSize: '11px', fontWeight: 600, padding: '8px' }}>{v == null ? '—' : v + '%'}</td>
								})}
							</tr>
						))}
					</tbody>
				</table></div>
			</div></div>

			{/* Pareto + Afinlik */}
			<div className="row g-3 mb-3">
				<div className="col-lg-5"><div className="card h-100"><div className="card-body">
					<h6 className="fw-bold mb-1">{t('pareto_title')}</h6>
					<p className="text-muted" style={{ fontSize: '12.5px' }}>{t('pareto_hint')}</p>
					<div className="text-center py-3">
						<div className="fw-bold" style={{ fontSize: '38px', color: '#7c3aed', letterSpacing: '-.02em' }}>{num(d.pareto.top20_share)}%</div>
						<div className="text-muted">{t('pareto_result')}</div>
					</div>
				</div></div></div>
				<div className="col-lg-7"><div className="card h-100"><div className="card-body">
					<h6 className="fw-bold mb-1">{t('affinity_title')}</h6>
					<p className="text-muted" style={{ fontSize: '12.5px' }}>{t('affinity_hint')}</p>
					<div className="table-responsive"><table className="table table-bordered mb-0" style={{ fontSize: '12.5px' }}>
						<thead><tr><th></th>{PTYPES.map(p => <th key={p.id} className="text-center">{p.k.slice(0, 6)}.</th>)}</tr></thead>
						<tbody>
							{affRows.map((r, i) => (
								<tr key={i}>
									<td className="fw-600">{r.name || t('region_no_type')}</td>
									{PTYPES.map(p => {
										const share = pct(num(r.pt[p.id]), r.total)
										return <td key={p.id} className="text-center" style={{ background: share > 0 ? `rgba(37,99,235,${0.08 + share / 100 * 0.7})` : '', color: share > 45 ? '#fff' : '#1e293b', fontWeight: share > 30 ? 700 : 400 }}>{share}%</td>
									})}
								</tr>
							))}
						</tbody>
					</table></div>
				</div></div></div>
			</div>

			{/* To'lov intizomi */}
			<div className="card mb-3"><div className="card-body">
				<h6 className="fw-bold mb-3">{t('payment_discipline')}</h6>
				<div className="table-responsive"><table className="table table-bordered mb-0">
					<thead><tr><th>{t('type')}</th><th className="text-center">{t('payment_pct')}</th><th className="text-center">{t('avg_delay')}</th><th className="text-center">{t('overdue')}</th><th className="text-center">{t('discipline')}</th></tr></thead>
					<tbody>
						{d.payment_discipline.map((p, i) => {
							const v = num(p.tolov_pct)
							const dis = v >= 85 ? { t: t('disc_excellent'), c: 'bg-success' } : v >= 70 ? { t: t('disc_good'), c: 'bg-success' } : v >= 55 ? { t: t('disc_medium'), c: 'bg-warning text-dark' } : { t: t('disc_low'), c: 'bg-danger' }
							return (
								<tr key={i}>
									<td className="fw-600">{p.segment || t('region_no_type')}</td>
									<td className="text-center fw-600">{v}%</td>
									<td className="text-center">{num(p.avg_overdue)} {t('day_short')}</td>
									<td className="text-center">{num(p.overdue)}</td>
									<td className="text-center"><span className={`badge ${dis.c}`}>{dis.t}</span></td>
								</tr>
							)
						})}
					</tbody>
				</table></div>
			</div></div>

			{/* Top mijozlar */}
			<div className="card"><div className="card-body">
				<h6 className="fw-bold mb-1">{t('top_clients')}</h6>
				<p className="text-muted mb-3" style={{ fontSize: '12.5px' }}>{t('top_clients_hint')}</p>
				<div className="table-responsive"><table className="table table-bordered mb-0">
					<thead><tr>
						<th>{t('client')}</th><th>{t('type')}</th><th className="text-center">{t('last_purchase')}</th><th className="text-center">{t('interval')}</th>
						<th className="text-center">{t('cheques')}</th><th className="text-end">{t('sales')}</th><th className="text-end">{t('profit')}</th><th className="text-end">{t('debt')}</th><th></th>
					</tr></thead>
					<tbody>
						{d.top_clients.map((c, i) => (
							<tr key={i} style={{ cursor: 'pointer' }} onClick={() => open360(c.client_id)}>
								<td className="fw-600">{c.name}</td><td>{c.segment || t('region_no_type')}</td>
								<td className="text-center" style={{ color: num(c.last_kun) > 60 ? '#d97706' : '#059669' }}>{num(c.last_kun)} {t('day_short')}</td>
								<td className="text-center text-muted">{c.oraliq != null ? num(c.oraliq) + ' ' + t('day_short') : '—'}</td>
								<td className="text-center">{num(c.freq)}</td>
								<td className="text-end text-nowrap">{shortMoney(c.savdo)}</td>
								<td className="text-end text-nowrap" style={{ color: '#059669' }}>{shortMoney(c.foyda)}</td>
								<td className="text-end text-nowrap" style={{ color: num(c.qarz) < 0 ? '#dc3545' : '#64748b' }}>{c.qarz ? shortMoney(c.qarz) : '—'}</td>
								<td className="text-center text-muted"><i className="uil-angle-right"></i></td>
							</tr>
						))}
					</tbody>
				</table></div>
			</div></div>

			{/* Mijoz 360° modal */}
			<Modal show={m360.show} animation={false} centered size="lg" onHide={() => setM360({ show: false, loading: false, profile: null, activity: [] })}>
				<Modal.Header closeButton>
					<Modal.Title style={{ fontSize: '17px' }}>
						<i className="uil-user-circle me-2 text-primary"></i>{t('client_360')}
						{m360.profile && <span className="text-muted ms-2" style={{ fontSize: '14px' }}>— {m360.profile.name}</span>}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{m360.loading && <div className="text-center py-4 text-muted"><span className="spinner-border spinner-border-sm me-2"></span>{t('loading')}</div>}
					{!m360.loading && m360.profile && (() => {
						const p = m360.profile
						const nextIn = (p.oraliq != null && p.jim_kun != null) ? num(p.oraliq) - num(p.jim_kun) : null
						const stat = (k, v, cl) => (
							<div className="d-flex justify-content-between py-1" style={{ borderBottom: '1px solid #f1f5f9', fontSize: '13px' }}>
								<span className="text-muted">{k}</span><span className="fw-600" style={cl ? { color: cl } : {}}>{v}</span>
							</div>
						)
						return (
							<div className="row g-3">
								<div className="col-md-5">
									<div className="d-flex align-items-center gap-2 mb-2">
										<span className="badge bg-primary" style={{ width: '46px', height: '46px', fontSize: '18px', display: 'grid', placeItems: 'center', borderRadius: '12px' }}>{(p.name || '?')[0]}</span>
										<div><div className="fw-bold">{p.name}</div><div className="text-muted" style={{ fontSize: '12px' }}>{p.phone}</div></div>
									</div>
									{stat(t('lifetime_sales'), shortMoney(p.savdo))}
									{stat(t('lifetime_profit'), shortMoney(p.foyda), '#059669')}
									{stat(t('current_debt'), p.qarz ? shortMoney(p.qarz) : '0', num(p.qarz) < 0 ? '#dc3545' : null)}
									{stat(t('cheques'), num(p.freq))}
									{stat(t('client_age'), Math.round(num(p.umr_kun) / 30) + ' ' + t('months_short'))}
									{stat(t('avg_interval'), p.oraliq != null ? num(p.oraliq) + ' ' + t('day_short') : '—')}
									{stat(t('next_expected'), nextIn != null ? (nextIn >= 0 ? '~' + nextIn + ' ' + t('day_short') : t('overdue_by') + ' ' + Math.abs(nextIn) + ' ' + t('day_short')) : '—', nextIn != null && nextIn < 0 ? '#dc3545' : '#d97706')}
								</div>
								<div className="col-md-7">
									<div className="fw-600 mb-2" style={{ fontSize: '13.5px' }}>{t('recent_activity')}</div>
									{m360.activity.map((a, i) => (
										<div key={i} className="d-flex gap-2 py-2" style={{ borderBottom: '1px solid #f1f5f9', fontSize: '13px' }}>
											<span style={{ color: '#94a3b8', width: '52px', flex: 'none' }}>{a.d?.slice(5)}</span>
											<div><b>#{a.id}</b> · {shortMoney(a.total_amount)} · <span className="text-muted">{a.turlar}</span>
												{a.payment_plan === 'nasiya' && <span className="badge bg-danger ms-1" style={{ fontSize: '10px' }}>{t('plan_nasiya')}</span>}
											</div>
										</div>
									))}
									{m360.activity.length === 0 && <div className="text-muted">{t('list_empty')}</div>}
								</div>
							</div>
						)
					})()}
				</Modal.Body>
			</Modal>

			{/* Segment mijozlari — RFM kartasi bosilganda */}
			<Modal show={mSeg.show} animation={false} centered size="lg" onHide={() => setMSeg({ show: false, loading: false, seg: '', title: '', clients: [] })}>
				<Modal.Header closeButton>
					<Modal.Title style={{ fontSize: '17px' }}>
						<i className="uil-users-alt me-2 text-primary"></i>{mSeg.title || t('segment_clients')}
						{!mSeg.loading && <span className="badge bg-primary ms-2">{mSeg.clients.length}</span>}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
					{mSeg.loading && <div className="text-center py-4 text-muted"><span className="spinner-border spinner-border-sm me-2"></span>{t('loading')}</div>}
					{!mSeg.loading && (
						<div className="table-responsive"><table className="table table-hover align-middle mb-0">
							<thead><tr style={{ fontSize: '12px' }}>
								<th>{t('client')}</th><th className="text-end">{t('days_silent')}</th>
								<th className="text-end">{t('cheques')}</th><th>{t('last_purchase')}</th>
								<th className="text-end">{t('debt')}</th><th></th>
							</tr></thead>
							<tbody style={{ fontSize: '13px' }}>
								{mSeg.clients.map((c, i) => (
									<tr key={i}>
										<td style={{ cursor: 'pointer' }} onClick={() => open360(c.client_id)}>
											<span className="fw-600 text-primary">{c.name}</span>
											{c.phone && <div className="text-muted" style={{ fontSize: '11.5px' }}>{c.phone}</div>}
										</td>
										<td className="text-end">{num(c.recency)} {t('day_short')}</td>
										<td className="text-end">{num(c.freq)}</td>
										<td className="text-muted">{c.last_sale}</td>
										<td className="text-end" style={{ color: num(c.qarz) < 0 ? '#dc3545' : '#64748b' }}>{num(c.qarz) ? shortMoney(c.qarz) : '—'}</td>
										<td className="text-end">
											{c.phone && <a href={`tel:${c.phone}`} className="btn btn-sm btn-outline-success py-0 px-2" onClick={e => e.stopPropagation()}><i className="uil-phone"></i> {t('call')}</a>}
										</td>
									</tr>
								))}
								{mSeg.clients.length === 0 && <tr><td colSpan="6" className="text-center text-muted py-3">{t('list_empty')}</td></tr>}
							</tbody>
						</table></div>
					)}
				</Modal.Body>
			</Modal>
		</>
	)
}

export default Index
